/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import ButtonBar from './components/ButtonBar'
import { BACK_TO_SAMS } from '@/configs/strings/sams-list'
import { samsUrls } from '@/configs/urls'
import DefaultDetailsItem from '@/components/items/DefaultDetailsItem'
import { setPreviewUrl } from '@/utils/items'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { getSam, listSam, unlistSam } from '@/services/sams'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import CustomHeader from './components/CustomHeader'
import { Flex } from '@chakra-ui/react'
import { useResponsive } from '@/hooks/useResponsive'
import ContentOptions from './components/ContentOptions'
import { contentOptions } from './handlers'
import { formatEmptyValue } from '@/utils/assets'

const Sam = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentOption, setCurrentOption] = useState()
  const { samId } = useParams()

  const { getAccessTokenSilently: getAccessToken } = useAuth0()

  const {
    itemsResponse,
    loading: isLoading,
    reloadItems
  } = useFetchItemsWithParamsV2({
    fetchService: getSam,
    fetchParams: { samId }
  })
  const onOptionChange = (option) => {
    setCurrentOption(contentOptions[option])
  }

  const onCloseOptions = () => { setCurrentOption(undefined) }

  const submitBuildLicenseTerms = async (data) => {
    console.info('Form Data', data)
    try {
      setIsSubmitting(true)
      await listSam(samId, data, getAccessToken)
      await reloadItems()
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }
  const unlistBuildLicenseTerms = async () => {
    try {
      setIsSubmitting(true)
      await unlistSam(samId, getAccessToken)
      await reloadItems()
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }
  const imageUrl = setPreviewUrl(itemsResponse?.coverUrl, itemsResponse?.fileType)
  const { flexDirection, flexGap } = useResponsive()
  const setItemId = itemId => itemId || 'N/A'
  const licenseHistory = itemsResponse?.licenseHistory

  return (
    <RegularTemplate
      loadingContent={isLoading }
      loadingScreen={isSubmitting}
      backUrl={`/${samsUrls.base}`}
      backLabel={BACK_TO_SAMS}
      childrenHeader={() =>
        <CustomHeader
          title={itemsResponse?.title}
          listed={!!itemsResponse?.listed}
          creators={formatEmptyValue(itemsResponse?.creators.join(', '))}
        />
      }
    >
      { !isLoading && itemsResponse &&
        <>
          <ButtonBar
            onOptionChange={onOptionChange}
            currentOption={currentOption}
          />
          <Flex
            flexDirection={flexDirection}
            gap={flexGap}
          >
            <DefaultDetailsItem
              imageUrl={imageUrl}
              details={[
                { label: 'Title', value: itemsResponse.title },
                { label: 'Creators', value: formatEmptyValue(itemsResponse.creators.join(', ')) },
                { label: 'Publisher', value: formatEmptyValue(itemsResponse.publishers.join(', ')) },
                { label: 'Asset ID', value: setItemId(itemsResponse.assetId) },
                { label: 'File Type', value: itemsResponse.fileType },
                { label: 'Created Date', value: itemsResponse.createdAt }
              ]}
            />
            <ContentOptions
              handleCloseListSam={() => setCurrentOption(undefined)}
              itemsResponse={itemsResponse}
              onCloseOptions={onCloseOptions}
              optionSelected={currentOption}
              reloadSam={reloadItems}
              samId={samId}
              submitBuildLicenseTerms={submitBuildLicenseTerms}
              unlistBuildLicenseTerms={unlistBuildLicenseTerms}
              licenseHistory={licenseHistory}
            />
          </Flex>
        </>
      }
    </RegularTemplate>
  )
}

export default Sam
